// Wave-specific chart interfaces for enhanced visualization

import type { Curve } from './toolData'
import type { WaveFileType } from '../lib/parsers/types'

/**
 * Pure curve reference - just identifies a curve, no styling
 * This is the clean, lightweight reference that should be used everywhere
 */
export interface CurveReference {
  /** File type containing the curve */
  fileType: WaveFileType
  /** Name of the curve within that file */
  curveName: string
}

/**
 * Styling properties for curve visualization
 * Separated from reference for clean architecture
 */
export interface CurveStyling {
  color?: string
  strokeThickness?: number
  lineStyle?: LineStyle
  yAxisId?: YAxisPosition
  visible?: boolean
  opacity?: number
  interpolation?: InterpolationMethod
  showDataPoints?: boolean
  pointStyle?: PointStyle
  pointSize?: number
  /** Optional display label to show in legends and tooltips */
  displayName?: string
}

/**
 * Curve reference with styling - used by charts that need both
 * Combines the clean reference with optional styling overrides
 */
export interface StyledCurveReference {
  /** The curve reference (fileType + curveName) */
  reference: CurveReference
  /** Optional styling overrides for this chart */
  styling?: CurveStyling
}

/**
 * Chart configuration - stores references to data, not data itself
 */
export interface ChartConfig {
  /** Unique identifier for this chart */
  id: string
  
  /** Chart title */
  title?: string
  
  /** References to curves with styling (not actual curve data) */
  curveReferences: StyledCurveReference[]
  
  // Chart dimensions and layout
  /** Chart height preset */
  heightPreset?: ChartHeightPreset
  
  // Visual settings
  /** Whether to show grid lines */
  showGrid?: boolean
  
  /** Whether to show crosshairs on hover */
  showCrosshairs?: boolean
  
  /** Whether to show tooltips on hover */
  showTooltips?: boolean
  
  /** Background color override */
  backgroundColor?: string
  
  /** Legend display mode: overlay on chart, bottom strip, or none */
  legendMode?: 'overlay' | 'bottom' | 'none'
  
  // Axis configurations (up to 4 Y-axes)
  yAxes?: {
    left?: YAxisConfig
    right?: YAxisConfig
    left2?: YAxisConfig
    right2?: YAxisConfig
  }
}

/**
 * Interpolation methods supported by Wave charts
 */
export type InterpolationMethod = 'linear' | 'stepBefore' | 'stepAfter' | 'stepMiddle' | 'spline' | 'none'

/**
 * Line styles for curve rendering (based on SciChart supported patterns)
 */
export type LineStyle = 'solid' | 'dashed' | 'dotted' | 'dashdot'

/**
 * Data point marker styles (only SciChart-supported markers)
 */
export type PointStyle = 'none' | 'circle' | 'square' | 'triangle' | 'cross'

/**
 * Y-axis positions (up to 4 axes as per PRD)
 */
export type YAxisPosition = 'left' | 'right' | 'left2' | 'right2'

/**
 * Enhanced curve interface extending base Curve with visualization properties
 */
export interface WaveCurve extends Curve {
  // Source tracking
  /** Source filename this curve originated from */
  sourceFile?: string
  
  // Visual styling
  /** Curve color (hex, rgb, or named color) */
  color?: string
  
  /** Line thickness in pixels (1-10) */
  strokeThickness?: number
  
  /** Line style pattern */
  lineStyle?: LineStyle
  
  // Data point styling
  /** Whether to show data point markers */
  showDataPoints?: boolean
  
  /** Style of data point markers */
  pointStyle?: PointStyle
  
  /** Size of data point markers in pixels */
  pointSize?: number
  
  // Interpolation and axis assignment
  /** Interpolation method for curve rendering */
  interpolation?: InterpolationMethod
  
  /** Which Y-axis this curve is assigned to */
  yAxisId?: YAxisPosition
  
  // Curve behavior
  /** Whether this curve is visible */
  visible?: boolean
  
  /** Opacity of the curve (0-1) */
  opacity?: number

  /** Optional display label to show in legends and tooltips */
  displayName?: string
}

/**
 * Configuration for a single Y-axis (V3 Schema)
 * 
 * Range-centric design: presence of `range` determines auto vs manual ranging
 * - range undefined = auto-range mode
 * - range defined = manual range mode with specified min/max
 */
export interface YAxisConfig {
  /** Axis label/title */
  label?: string
  
  /** Unit of measurement */
  unit?: string
  
  /** Manual range - undefined = auto-range, defined = manual range */
  range?: { min: number; max: number }
  
  /** Axis color */
  color?: string
  
  /** Whether to show grid lines for this axis */
  showGridLines?: boolean
}

// XAxisConfig removed - all X-axes are datetime-based now

/**
 * Single chart with resolved data for rendering (used by WaveChartCanvas)
 * This contains actual curve data, not references - distinct from ChartConfig
 */
export interface WaveChart {
  /** Unique identifier for this chart */
  id: string
  
  /** Chart title */
  title?: string
  
  /** Curves displayed in this chart */
  curves: WaveCurve[]
  
  // Chart dimensions and layout
  /** Chart height preset */
  heightPreset?: ChartHeightPreset
  
  /** Legend corner for overlay legend */
  legendCorner?: 'tl' | 'tr' | 'bl' | 'br'
  /** Legend display mode: overlay on chart, bottom strip, or none */
  legendMode?: 'overlay' | 'bottom' | 'none'
  
  // Visual settings
  /** Whether to show grid lines */
  showGrid?: boolean
  
  /** Whether to show crosshairs on hover */
  showCrosshairs?: boolean
  
  /** Whether to show tooltips on hover */
  showTooltips?: boolean
  
  /** Background color override */
  backgroundColor?: string
  
  // Axis configurations (up to 4 Y-axes)
  yAxes?: {
    left?: YAxisConfig
    right?: YAxisConfig
    left2?: YAxisConfig
    right2?: YAxisConfig
  }
}



/**
 * Chart height presets - predefined sizes for consistency
 */
export type ChartHeightPreset = 'small' | 'medium' | 'large' | 'xl'

/**
 * Chart height preset configurations
 */
export const ChartHeightPresets = {
  small: { height: 200, label: 'Small' },
  medium: { height: 400, label: 'Medium' }, // Default
  large: { height: 600, label: 'Large' },
  xl: { height: 800, label: 'Extra Large' }
} as const

/**
 * Default styling constants
 */
export const WaveChartDefaults = {
  // Curve defaults
  strokeThickness: 2,
  interpolation: 'linear' as InterpolationMethod,
  lineStyle: 'solid' as LineStyle,
  pointStyle: 'none' as PointStyle,
  pointSize: 4,
  opacity: 1.0,
  
  // Chart defaults
  heightPreset: 'medium' as ChartHeightPreset,
  height: 400, // Legacy fallback, use heightPreset instead
  showGrid: true,
  showCrosshairs: true,
  showTooltips: true,
  
  // Canvas defaults
  enableXAxisSync: true,
  
  // Color palette for automatic curve coloring
  colors: [
    '#90caf9', // Primary blue (matches MUI primary)
    '#f48fb1', // Secondary pink (matches MUI secondary)  
    '#66bb6a', // Success green
    '#ffa726', // Warning orange
    '#f44336', // Error red
    '#29b6f6', // Info blue
    '#ab47bc', // Purple
    '#26c6da', // Cyan
    '#d4e157', // Lime
    '#ff7043', // Deep orange
  ] as const
} as const

/**
 * Type for default color palette
 */
export type WaveChartColor = typeof WaveChartDefaults.colors[number] 

/**
 * Curve ordering utilities
 * 
 * Wave uses a simple approach for curve Z-ordering/layering:
 * - The order of curves in ChartConfig.curveReferences[] determines render order
 * - Curves are rendered back-to-front (first in array renders behind others)
 * - SciChart renders curves in the exact order they're added to renderableSeries
 * 
 * For drag & drop operations:
 * - Moving curves between axes: Update curve.styling.yAxisId + reorder array
 * - Reordering within same axis: Just reorder the curveReferences array
 */

/**
 * Helper type for identifying curves during drag & drop operations
 * This is just an alias for CurveReference since they're the same thing now
 */
export type CurveIdentifier = CurveReference;

/**
 * Creates a unique identifier string for a curve reference
 */
export function getCurveId(curveRef: CurveReference): string {
  return `${curveRef.fileType}:${curveRef.curveName}`
}

/**
 * Creates a CurveIdentifier from a CurveReference
 * Since they're the same type now, this just returns the reference
 */
export function getCurveIdentifier(curveRef: CurveReference): CurveIdentifier {
  return curveRef;
}

/**
 * Creates a CurveReference from a StyledCurveReference
 */
export function getCleanReference(styledRef: StyledCurveReference): CurveReference {
  return styledRef.reference;
}

/**
 * Creates a StyledCurveReference from a clean CurveReference
 */
export function addStyling(reference: CurveReference, styling?: CurveStyling): StyledCurveReference {
  return { reference, styling };
}

/**
 * Finds a styled curve reference by clean reference
 */
export function findStyledCurveReference(
  styledReferences: StyledCurveReference[], 
  reference: CurveReference
): StyledCurveReference | undefined {
  return styledReferences.find(styledRef => 
    styledRef.reference.fileType === reference.fileType && 
    styledRef.reference.curveName === reference.curveName
  )
}

/**
 * Finds a curve reference by identifier (same as finding by reference now)
 */
export function findCurveReference(
  curveReferences: CurveReference[], 
  identifier: CurveIdentifier
): CurveReference | undefined {
  return curveReferences.find(ref => 
    ref.fileType === identifier.fileType && 
    ref.curveName === identifier.curveName
  )
}

/**
 * Helper to create curve ID string for unique identification
 */
export const createCurveId = (filename: string, curveName: string): string => 
  `${filename}:${curveName}` 
